import React, { useEffect, useMemo, useState } from 'react';
import { NotificationManager } from 'react-notifications';
import { Quantum } from 'ldrs/react';
import { handleWheel, handleMouseEnter, handleMouseLeave } from '../utils/Scroll';
import 'ldrs/react/Quantum.css';
import DashboardFooter from '../components/DashboardFooter';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December',];

const pad = (n) => String(n).padStart(2, '0');

const toISO = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
    )}`;

const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

const isActiveStatus = (status) => {
    if (status === undefined || status === null) return true;

    if (typeof status === 'boolean') {
        return status;
    }

    return Number(status) !== 0;
};

// Formats a "HH:MM" time string into a 12-hour display label, e.g. "09:00" -> "9:00 AM"
const formatTimeLabel = (time) => {
    if (!time) return '';

    const [hStr, mStr] = time.split(':');
    const h = Number(hStr);
    const m = Number(mStr);

    if (Number.isNaN(h) || Number.isNaN(m)) return time;

    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 === 0 ? 12 : h % 12;

    return `${hour12}:${pad(m)} ${period}`;
};

const timeRangeLabel = (start, end) => {
    if (!start && !end) return '';
    if (start && end) return `${formatTimeLabel(start)} – ${formatTimeLabel(end)}`;
    return formatTimeLabel(start || end);
};

// Normalised "HH:MM|HH:MM" key so "10:28:00" (API) and "10:28" (time input) compare equal.
const timeKey = (start, end) =>
    `${(start || '').slice(0, 5)}|${(end || '').slice(0, 5)}`;

// Converts a "HH:MM" value from <input type="time"> into "HH:MM:SS" for the API.
// Returns null when no time is set.
const formatTimeForApi = (time) => {
    if (!time) return null;
    return time.length === 5 ? `${time}:00` : time;
};


const buildDayOffPayload = (date, isOff, startTime, endTime, extra = {}) => ({
    date,
    status: 1,
    is_off: isOff ? 1 : 0,
    shift_start_time: isOff ? null : formatTimeForApi(startTime),
    shift_end_time: isOff ? null : formatTimeForApi(endTime),
    ...extra,
});

const isOffValue = (is_off) => {
    if (is_off === undefined || is_off === null) return true;
    if (typeof is_off === 'boolean') return is_off;
    return Number(is_off) === 1;
};

const buildMonthGrid = (year, month) => {
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay();

    const gridStart = new Date(
        year,
        month,
        1 - startOffset
    );

    const cells = [];

    for (let i = 0; i < 42; i += 1) {
        const d = new Date(gridStart);
        d.setDate(gridStart.getDate() + i);
        cells.push(d);
    }

    return cells;
};

const datesForWeekdayInRange = (weekday, startISO, endISO) => {
    if (!startISO || !endISO) return [];

    const start = new Date(`${startISO}T00:00:00`);
    const end = new Date(`${endISO}T00:00:00`);

    if (start > end) return [];

    const cursor = new Date(start);

    const offset =
        (weekday - cursor.getDay() + 7) % 7;

    cursor.setDate(cursor.getDate() + offset);

    const dates = [];

    while (cursor <= end) {
        dates.push(toISO(cursor));
        cursor.setDate(cursor.getDate() + 7);
    }
    return dates;
};

const EmployeeDayOff = () => {
    const [employees, setEmployees] = useState([]);
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [dayOffsMap, setDayOffsMap] = useState({});
    const today = useMemo(() => new Date(), []);
    const [viewYear, setViewYear] = useState(today.getFullYear());
    const [viewMonth, setViewMonth] = useState(today.getMonth());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showRecurringPicker, setShowRecurringPicker] = useState(false);
    const [recurringWeekday, setRecurringWeekday] = useState(1);
    const [recurringStart, setRecurringStart] = useState(() =>
        toISO(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [recurringEnd, setRecurringEnd] = useState(() =>
        toISO(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
    // 'off' = full day off (is_off: 1, no times sent)
    // 'shift' = not a day off, just custom shift hours (is_off: 0, times required)
    const [recurringMode, setRecurringMode] = useState('off');
    // Optional time window applied to recurring-weekday day offs (only used when recurringMode === 'shift')
    const [recurringStartTime, setRecurringStartTime] = useState('');
    const [recurringEndTime, setRecurringEndTime] = useState('');

    // Default shift hours applied to every working day of the visible month
    // that does NOT have its own custom shift time.
    const [shiftStartTime, setShiftStartTime] = useState('');
    const [shiftEndTime, setShiftEndTime] = useState('');

    // Pending (unsaved) date toggles, keyed by employee id -> Set of ISO date strings
    const [pendingToggles, setPendingToggles] = useState({});

    // ---- Custom shift times for specific days -------------------------------
    // customMode: when true, clicking calendar dates selects them for a custom
    // shift instead of toggling day offs.
    const [customMode, setCustomMode] = useState(false);
    const [customSelection, setCustomSelection] = useState([]); // array of ISO dates
    const [customStartTime, setCustomStartTime] = useState('');
    const [customEndTime, setCustomEndTime] = useState('');
    // Pending (unsaved) custom shift times: employeeId -> { [iso]: { start_time, end_time } }
    const [pendingCustom, setPendingCustom] = useState({});

    // Edit state
    const [editingId, setEditingId] = useState(null);
    const [editDate, setEditDate] = useState('');
    const [editMode, setEditMode] = useState('off');
    const [editStartTime, setEditStartTime] = useState('');
    const [editEndTime, setEditEndTime] = useState('');
    const token = localStorage.getItem('token');

    const authHeaders = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
    };

    const fetchEmployees = async () => {
        try {
            setLoading(true);

            const res = await fetch(
                `${process.env.REACT_APP_HOST_API_URL}/app/employes-list/`,
                {
                    method: 'POST',
                    headers: authHeaders,
                    body: JSON.stringify({}),
                }
            );

            if (!res.ok) {
                throw new Error('Failed to fetch employees');
            }

            const data = await res.json();

            const list = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
            setEmployees(list);

            if (list.length && selectedEmployeeId === null) {
                setSelectedEmployeeId(list[0].id);
            }
        } catch (err) {
            console.error('Employee fetch error:', err);
            NotificationManager.error('Error fetching employees');
        } finally {
            setLoading(false);
        }
    };

    const fetchDayOffs = async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_HOST_API_URL}/admin-user/employee-day-off/`,
                {
                    method: 'GET',
                    headers: authHeaders,
                }
            );

            if (!res.ok) {
                throw new Error('Failed to fetch day offs');
            }

            const data = await res.json();
            console.log('Fetched day offs:', data);

            const list = Array.isArray(data) ? data : Array.isArray(data.employees) ? data.employees : Array.isArray(data.results)
                ? data.results : Array.isArray(data.data) ? data.data : [];

            const merged = {};

            list.forEach((entry) => {
                if (Array.isArray(entry.day_offs)) {
                    const activeDayOffs = entry.day_offs.filter((d) => isActiveStatus(d.status))
                        .map((d) => ({
                            id: d.id,
                            date: d.date,
                            is_off: isOffValue(d.is_off),
                            start_time: d.start_time || d.shift_start_time || '',
                            end_time: d.end_time || d.shift_end_time || '',
                            updated_at: d.updated_at,
                        }));

                    const uniqueByDate =
                        Object.values(
                            activeDayOffs.reduce(
                                (acc, item) => {
                                    const existing =
                                        acc[item.date];

                                    if (!existing || new Date(item.updated_at || 0) >
                                        new Date(existing.updated_at || 0)) {
                                        acc[item.date] = item;
                                    }

                                    return acc;
                                },
                                {}
                            )
                        );

                    merged[entry.employee] = uniqueByDate.map(({ id, date, is_off, start_time, end_time }) => ({ id, date, is_off, start_time, end_time, }));
                } else if (
                    entry.employee && entry.date && isActiveStatus(entry.status)
                ) {
                    if (!merged[entry.employee]) {
                        merged[entry.employee] = [];
                    }

                    merged[entry.employee].push({
                        id: entry.id,
                        date: entry.date,
                        is_off: isOffValue(entry.is_off),
                        start_time: entry.start_time || entry.shift_start_time || '',
                        end_time: entry.end_time || entry.shift_end_time || '',
                    });
                }
            });

            setDayOffsMap(merged);
        } catch (err) {
            console.error('Day-off fetch error:', err);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            await fetchEmployees();
            await fetchDayOffs();
        };

        loadData();
    }, []);

    const postDayOffChanges = async (employeeId, items) => {
        const payload = {
            employees: [
                {
                    employee: Number(employeeId),
                    day_offs: items,
                },
            ],
        };

        setSaving(true);

        try {
            const res = await fetch(
                `${process.env.REACT_APP_HOST_API_URL}/admin-user/employee-day-off/`,
                {
                    method: 'POST',
                    headers: authHeaders,
                    body: JSON.stringify(payload),
                }
            );

            const data = await res.json().catch(() => null);
            console.log('POST day off response:', data);

            if (!res.ok) {
                const msg = data?.message || data?.error || data?.detail ||
                    `Request failed (${res.status})`;
                throw new Error(msg);
            }

            return data;
        } finally {
            setSaving(false);
        }
    };

    const selectedEmployee = employees.find((e) =>
        Number(e.id) === Number(selectedEmployeeId)
    );

    const selectEmployee = (id) => {
        setSelectedEmployeeId(id);
        setCustomSelection([]);
    };

    // All saved (persisted) records for the selected employee -- day offs AND shift days
    const selectedRecords = useMemo(() => {
        const offs = dayOffsMap[selectedEmployeeId] || [];

        const unique = new Map();

        offs.forEach((item) => {
            if (!unique.has(item.date)) {
                unique.set(item.date, item);
            }
        });

        return Array.from(unique.values());
    }, [dayOffsMap, selectedEmployeeId]);

    // Saved (persisted) day-off dates only, used for calendar highlighting
    const selectedOffs = useMemo(
        () => selectedRecords.filter((o) => o.is_off),
        [selectedRecords]
    );

    // Quick lookup: date -> saved record (day off or shift), so a month sync
    // can reuse the existing id instead of creating a duplicate row.
    const selectedRecordByDate = useMemo(() => {
        const map = new Map();
        selectedRecords.forEach((r) => map.set(r.date, r));
        return map;
    }, [selectedRecords]);

    // The "base" shift for the visible month = the most common saved shift time.
    // Saved shift days whose time differs from it are treated as custom days, so a
    // month save keeps them instead of overwriting them with the default time.
    const baseShiftKey = useMemo(() => {
        const prefix = `${viewYear}-${pad(viewMonth + 1)}-`;
        const counts = {};

        selectedRecords.forEach((r) => {
            if (!r.is_off && r.start_time && r.end_time && r.date.startsWith(prefix)) {
                const k = timeKey(r.start_time, r.end_time);
                counts[k] = (counts[k] || 0) + 1;
            }
        });

        let best = '';
        let bestCount = 0;

        Object.entries(counts).forEach(([k, c]) => {
            if (c > bestCount) {
                best = k;
                bestCount = c;
            }
        });

        return best;
    }, [selectedRecords, viewYear, viewMonth]);

    // Pre-fill the default shift inputs with the month's base shift when data loads
    // or the employee / month changes.
    useEffect(() => {
        if (!baseShiftKey) return;

        const [s, e] = baseShiftKey.split('|');
        setShiftStartTime(s);
        setShiftEndTime(e);
    }, [selectedEmployeeId, viewYear, viewMonth, baseShiftKey]);

    // Pending (unsaved) toggles for the selected employee
    const currentPending = useMemo(
        () => pendingToggles[selectedEmployeeId] || new Set(),
        [pendingToggles, selectedEmployeeId]
    );

    const currentPendingCustom = useMemo(
        () => pendingCustom[selectedEmployeeId] || {},
        [pendingCustom, selectedEmployeeId]
    );

    const hasUnsavedChanges =
        currentPending.size > 0 || Object.keys(currentPendingCustom).length > 0;

    // Effective list = saved offs with pending removals/additions merged in, for display
    const effectiveOffs = useMemo(() => {
        const savedDates = new Set(selectedOffs.map((o) => o.date));
        const result = [];

        selectedOffs.forEach((o) => {
            const willRemove = currentPending.has(o.date);
            result.push({ ...o, pending: willRemove, willRemove, willAdd: false });
        });

        currentPending.forEach((iso) => {
            if (!savedDates.has(iso)) {
                result.push({
                    id: null,
                    date: iso,
                    is_off: true,
                    start_time: '',
                    end_time: '',
                    pending: true,
                    willRemove: false,
                    willAdd: true,
                });
            }
        });

        return result.sort((a, b) => a.date.localeCompare(b.date));
    }, [selectedOffs, currentPending]);

    const effectiveActiveCount = effectiveOffs.filter((o) => !o.willRemove).length;

    // Saved shift days (is_off === false) with pending state merged in:
    // - pending custom time replaces the saved time (pendingCustom: true)
    // - a shift date that was clicked on the calendar will become a day off (willBecomeOff)
    // - a pending custom time on a date with no saved shift yet is shown as a new entry
    const effectiveShifts = useMemo(() => {
        const result = [];
        const savedShiftDates = new Set();

        selectedRecords
            .filter((r) => !r.is_off)
            .forEach((r) => {
                savedShiftDates.add(r.date);

                const custom = currentPendingCustom[r.date];
                const willBecomeOff = currentPending.has(r.date);

                result.push({
                    ...r,
                    start_time: custom ? custom.start_time : r.start_time,
                    end_time: custom ? custom.end_time : r.end_time,
                    pendingCustom: !!custom,
                    willBecomeOff,
                    pending: willBecomeOff || !!custom,
                });
            });

        const savedOffDates = new Set(selectedOffs.map((o) => o.date));

        Object.entries(currentPendingCustom).forEach(([iso, t]) => {
            if (savedShiftDates.has(iso)) return;

            // Effective off state of a date that has no saved shift record
            const off = currentPending.has(iso)
                ? !savedOffDates.has(iso)
                : savedOffDates.has(iso);

            if (off) return;

            result.push({
                id: null,
                date: iso,
                is_off: false,
                start_time: t.start_time,
                end_time: t.end_time,
                pendingCustom: true,
                willBecomeOff: false,
                pending: true,
            });
        });

        return result.sort((a, b) => a.date.localeCompare(b.date));
    }, [selectedRecords, selectedOffs, currentPending, currentPendingCustom]);

    // Shift days grouped by their shift start/end time, e.g.
    //   10:28 AM – 4:28 PM  -> [2026-09-01, 2026-09-03, ...]
    //   2:00 PM – 10:00 PM  -> [2026-09-10, ...]
    const shiftGroups = useMemo(() => {
        const groups = new Map();

        effectiveShifts.forEach((o) => {
            const hasTime = o.start_time || o.end_time;
            const key = hasTime ? timeKey(o.start_time, o.end_time) : 'none';

            if (!groups.has(key)) {
                groups.set(key, {
                    key,
                    label: timeRangeLabel(o.start_time, o.end_time) || 'No time set',
                    sortKey: hasTime ? key : '~',
                    items: [],
                });
            }

            groups.get(key).items.push(o);
        });

        return Array.from(groups.values()).sort((a, b) =>
            a.sortKey.localeCompare(b.sortKey)
        );
    }, [effectiveShifts]);

    // date -> 'saved' | 'pending' for working days that have a custom (non-base) shift time,
    // used to mark those days on the calendar.
    const customTimeDates = useMemo(() => {
        const map = new Map();

        selectedRecords.forEach((r) => {
            if (
                !r.is_off && r.start_time && r.end_time &&
                baseShiftKey && timeKey(r.start_time, r.end_time) !== baseShiftKey
            ) {
                map.set(r.date, 'saved');
            }
        });

        Object.keys(currentPendingCustom).forEach((iso) => map.set(iso, 'pending'));

        return map;
    }, [selectedRecords, baseShiftKey, currentPendingCustom]);

    const isDateOff = (date) => {
        const iso = toISO(date);
        const savedActive = selectedOffs.some((o) => o.date === iso);
        const pending = currentPending.has(iso);
        return pending ? !savedActive : savedActive;
    };

    const extractRecordsFlat = (data) => {
        if (Array.isArray(data)) {
            return data;
        }

        if (Array.isArray(data?.employees)) {
            return data.employees.flatMap((e) =>
                (e.day_offs || []).map((d) => ({ ...d, employee: e.employee, }))
            );
        }

        if (Array.isArray(data?.results)
        ) {
            return data.results;
        }

        if (Array.isArray(data?.data)
        ) {
            return data.data;
        }

        if (Array.isArray(data?.created)
        ) {
            return data.created;
        }

        if (Array.isArray(data?.updated)
        ) {
            return data.updated;
        }

        return [];
    };

    const findSavedRecord = (data, employeeId, date) => {
        const records = extractRecordsFlat(data);

        const matches = records.filter((r) => Number(r.employee) === Number(employeeId) && r.date === date);

        if (!matches.length) {
            return undefined;
        }

        return matches.reduce((latest, r) => {
            if (!latest) {
                return r;
            }

            const latestKey = latest.updated_at || latest.created_at || latest.id;

            const rKey = r.updated_at || r.created_at || r.id;

            return rKey > latestKey ? r : latest;
        },
            undefined
        );
    };

    // Toggle a single ISO date in the pending set for the selected employee (local only, no API call)
    const toggleIso = (iso) => {
        if (!selectedEmployeeId) return;

        setPendingToggles((prev) => {
            const set = new Set(prev[selectedEmployeeId] || []);

            if (set.has(iso)) {
                set.delete(iso);
            } else {
                set.add(iso);
            }

            return { ...prev, [selectedEmployeeId]: set };
        });
    };

    const handleDateClick = (date) => {
        // Custom shift mode: pick working days of the visible month for a custom shift time
        if (customMode) {
            if (date.getMonth() !== viewMonth) return;

            if (isDateOff(date)) {
                NotificationManager.warning("Day off dates can't have a custom shift");
                return;
            }

            const iso = toISO(date);

            setCustomSelection((prev) =>
                prev.includes(iso) ? prev.filter((d) => d !== iso) : [...prev, iso]
            );
            return;
        }

        toggleIso(toISO(date));
    };

    const discardChanges = () => {
        if (!selectedEmployeeId) return;

        setPendingToggles((prev) => {
            const copy = { ...prev };
            delete copy[selectedEmployeeId];
            return copy;
        });

        setPendingCustom((prev) => {
            const copy = { ...prev };
            delete copy[selectedEmployeeId];
            return copy;
        });

        setCustomSelection([]);
    };

    const validateTimeRange = (start, end) => {
        if (start && end && start >= end) {
            NotificationManager.error('Start time must be before end time');
            return false;
        }
        return true;
    };

    // Stores the custom start/end time for every selected date (local only).
    // The times are sent to the API when the month is saved.
    const applyCustomShift = () => {
        if (!selectedEmployeeId) return;

        if (!customSelection.length) {
            NotificationManager.error('Click one or more working days on the calendar first');
            return;
        }

        if (!customStartTime || !customEndTime) {
            NotificationManager.error('Enter a custom shift start and end time');
            return;
        }

        if (!validateTimeRange(customStartTime, customEndTime)) return;

        setPendingCustom((prev) => {
            const forEmployee = { ...(prev[selectedEmployeeId] || {}) };

            customSelection.forEach((iso) => {
                forEmployee[iso] = {
                    start_time: customStartTime,
                    end_time: customEndTime,
                };
            });

            return { ...prev, [selectedEmployeeId]: forEmployee };
        });

        NotificationManager.success(
            `Custom shift set for ${customSelection.length} day(s). Press Save Month to apply.`
        );

        setCustomSelection([]);
    };

    // Undo one pending custom shift time
    const removePendingCustom = (iso) => {
        setPendingCustom((prev) => {
            const forEmployee = { ...(prev[selectedEmployeeId] || {}) };
            delete forEmployee[iso];

            const copy = { ...prev };

            if (Object.keys(forEmployee).length) {
                copy[selectedEmployeeId] = forEmployee;
            } else {
                delete copy[selectedEmployeeId];
            }

            return copy;
        });
    };

    // Saves the whole visible month in one go:
    // - dates the user clicked are sent as a day off (is_off: 1)
    // - every other day is sent as a shift day (is_off: 0) using, in order of priority:
    //     1. a pending custom time for that day,
    //     2. its already-saved custom time (differs from the month's base shift),
    //     3. the default shiftStartTime / shiftEndTime pair.
    const saveChanges = async () => {
        if (!selectedEmployeeId) return;

        const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

        const monthDates = [];
        for (let day = 1; day <= daysInMonth; day += 1) {
            monthDates.push(new Date(viewYear, viewMonth, day));
        }

        const monthItems = monthDates.map((d) => {
            const iso = toISO(d);

            if (isDateOff(d)) {
                return { iso, off: true, start: '', end: '' };
            }

            const custom = currentPendingCustom[iso];

            if (custom) {
                return { iso, off: false, start: custom.start_time, end: custom.end_time };
            }

            const existing = selectedRecordByDate.get(iso);

            const existingIsCustom =
                existing && !existing.is_off && existing.start_time && existing.end_time &&
                timeKey(existing.start_time, existing.end_time) !== baseShiftKey;

            if (existingIsCustom) {
                return { iso, off: false, start: existing.start_time, end: existing.end_time };
            }

            return { iso, off: false, start: shiftStartTime, end: shiftEndTime };
        });

        const missingTimes = monthItems.some((m) => !m.off && (!m.start || !m.end));

        if (missingTimes) {
            NotificationManager.error('Enter a default shift start and end time for the working days first');
            return;
        }

        if (!validateTimeRange(shiftStartTime, shiftEndTime)) return;

        const items = monthItems.map(({ iso, off, start, end }) => {
            const existing = selectedRecordByDate.get(iso);
            return buildDayOffPayload(
                iso,
                off,
                start,
                end,
                existing?.id ? { id: existing.id } : {}
            );
        });

        try {
            const data = await postDayOffChanges(selectedEmployeeId, items);

            setDayOffsMap((prev) => {
                const current = prev[selectedEmployeeId] || [];
                const monthDateSet = new Set(monthItems.map((m) => m.iso));

                // Keep records outside this month untouched, replace this month's records
                const outsideMonth = current.filter((o) => !monthDateSet.has(o.date));

                const thisMonth = monthItems.map(({ iso, off, start, end }) => {
                    const saved = findSavedRecord(data, selectedEmployeeId, iso);
                    const existing = selectedRecordByDate.get(iso);

                    return {
                        id: saved?.id || existing?.id,
                        date: iso,
                        is_off: off,
                        start_time: off ? '' : (saved?.start_time || saved?.shift_start_time || start || ''),
                        end_time: off ? '' : (saved?.end_time || saved?.shift_end_time || end || ''),
                    };
                });

                return { ...prev, [selectedEmployeeId]: [...outsideMonth, ...thisMonth] };
            });

            setPendingToggles((prev) => {
                const copy = { ...prev };
                delete copy[selectedEmployeeId];
                return copy;
            });

            // Clear only this month's pending custom times
            const monthPrefix = `${viewYear}-${pad(viewMonth + 1)}-`;

            setPendingCustom((prev) => {
                const forEmployee = {};

                Object.entries(prev[selectedEmployeeId] || {}).forEach(([iso, t]) => {
                    if (!iso.startsWith(monthPrefix)) forEmployee[iso] = t;
                });

                const copy = { ...prev };

                if (Object.keys(forEmployee).length) {
                    copy[selectedEmployeeId] = forEmployee;
                } else {
                    delete copy[selectedEmployeeId];
                }

                return copy;
            });

            setCustomSelection([]);

            const offCount = monthItems.filter((m) => m.off).length;

            NotificationManager.success(
                `Saved: ${offCount} day(s) off, ${monthItems.length - offCount} shift day(s)`
            );
        } catch (err) {
            console.error('Save day offs error:', err);
            NotificationManager.error(err.message || 'Error saving day offs');
        }
    };

    const startEdit = (record) => {
        if (!record.id) {
            NotificationManager.warning('Day off ID not found');
            return;
        }

        setEditingId(record.id);
        setEditDate(record.date);
        setEditMode(record.is_off === false ? 'shift' : 'off');
        setEditStartTime((record.start_time || '').slice(0, 5));
        setEditEndTime((record.end_time || '').slice(0, 5));
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditDate('');
        setEditMode('off');
        setEditStartTime('');
        setEditEndTime('');
    };

    const saveEdit = async (employeeId) => {
        if (!editDate) {
            NotificationManager.error('Pick a date');
            return;
        }

        const editIsOff = editMode === 'off';

        if (!editIsOff) {
            if (!editStartTime || !editEndTime) {
                NotificationManager.error('Enter both a start and end shift time, or switch to "Full day off"');
                return;
            }
            if (!validateTimeRange(editStartTime, editEndTime)) return;
        }

        const current = dayOffsMap[employeeId] || [];

        const duplicate = current.find((o) =>
            o.date === editDate && Number(o.id) !== Number(editingId)
        );

        if (duplicate) {
            NotificationManager.error('That date already has a record for this employee');
            return;
        }

        try {
            await postDayOffChanges(employeeId,
                [buildDayOffPayload(editDate, editIsOff, editStartTime, editEndTime, { id: editingId }),]
            );

            setDayOffsMap((prev) => ({
                ...prev, [employeeId]: (prev[employeeId] || []
                ).map((o) => Number(o.id) === Number(editingId) ?
                    {
                        ...o,
                        date: editDate,
                        is_off: editIsOff,
                        start_time: editIsOff ? '' : editStartTime,
                        end_time: editIsOff ? '' : editEndTime,
                    } : o),
            })
            );

            cancelEdit();

            NotificationManager.success('Day updated successfully');
        } catch (err) {
            console.error('Edit day off error:', err);

            NotificationManager.error(err.message || 'Error updating day off');
        }
    };

    const applyRecurringWeekday =
        async () => {
            if (!selectedEmployeeId) {
                return;
            }

            if (!recurringStart || !recurringEnd) {
                NotificationManager.error('Pick a start and end date');
                return;
            }

            const recurringIsOff = recurringMode === 'off';

            if (!recurringIsOff) {
                if (!recurringStartTime || !recurringEndTime) {
                    NotificationManager.error('Enter both a start and end shift time, or switch to "Full day off"');
                    return;
                }
                if (!validateTimeRange(recurringStartTime, recurringEndTime)) return;
            }

            const newDates = datesForWeekdayInRange(
                recurringWeekday, recurringStart, recurringEnd
            );

            if (!newDates.length) {
                NotificationManager.error('No matching dates in that range');
                return;
            }

            const current = dayOffsMap[selectedEmployeeId] || [];


            const existingDates = new Set(
                current.map((o) => o.date)
            );

            // Remove duplicate dates from generated dates
            const uniqueNewDates = [...new Set(newDates)];

            const toAdd = uniqueNewDates.filter(
                (date) => !existingDates.has(date)
            );

            if (!toAdd.length) {
                NotificationManager.success('Those dates are already selected');
                return;
            }

            try {
                const data = await postDayOffChanges(
                    selectedEmployeeId,
                    toAdd.map((date) =>
                        buildDayOffPayload(date, recurringIsOff, recurringStartTime, recurringEndTime)
                    )
                );

                const newRecords = toAdd.map((date) => {
                    const saved = findSavedRecord(data, selectedEmployeeId, date);

                    return {
                        id: saved?.id,
                        date,
                        is_off: recurringIsOff,
                        start_time: recurringIsOff ? '' : (saved?.start_time || saved?.shift_start_time || recurringStartTime || ''),
                        end_time: recurringIsOff ? '' : (saved?.end_time || saved?.shift_end_time || recurringEndTime || ''),
                    };
                }
                );

                setDayOffsMap((prev) => {
                    const existing = prev[selectedEmployeeId] || [];

                    const existingDates = new Set(
                        existing.map((item) => item.date)
                    );

                    const uniqueRecords = newRecords.filter(
                        (item) => !existingDates.has(item.date)
                    );

                    return {
                        ...prev,
                        [selectedEmployeeId]: [...existing, ...uniqueRecords,],
                    };
                });

                NotificationManager.success(
                    `Saved ${toAdd.length} ${WEEKDAYS[recurringWeekday]} ${recurringIsOff ? 'day off(s)' : 'shift day(s)'}`
                );
            } catch (err) {
                console.error(err);

                NotificationManager.error(err.message || 'Error saving day offs');
            }
        };

    const changeMonth = (delta) => {
        let m = viewMonth + delta;
        let y = viewYear;

        if (m < 0) {
            m = 11;
            y -= 1;
        }

        if (m > 11) {
            m = 0;
            y += 1;
        }

        setViewMonth(m);
        setViewYear(y);
        setCustomSelection([]);
    };

    const monthGrid = useMemo(() =>
        buildMonthGrid(viewYear, viewMonth),
        [viewYear, viewMonth]
    );

    const filteredEmployees = employees.filter((e) =>
        (e.full_name || e.name || '')
            .toLowerCase().includes(employeeSearch.toLowerCase())
    );

    // Renders one badge for either a day-off record or a shift record,
    // including the inline editor when that record is being edited.
    // showTime = false hides the time on the badge (used inside the shift groups,
    // where the group heading already shows the time).
    const renderRecordBadge = (o, showTime = true) => {
        const isShift = o.is_off === false;
        const key = `${isShift ? 'shift' : 'off'}-${o.id ?? o.date}`;
        const isEditing = o.id != null && !o.pending && Number(editingId) === Number(o.id);

        if (isEditing) {
            return (
                <span key={key}
                    className="d-flex align-items-center gap-1 flex-wrap"
                    style={{
                        backgroundColor: '#fff8e1', border: '1px solid #f0c96a',
                        borderRadius: 6, padding: '4px 8px',
                    }}>
                    <input type="date"
                        value={editDate}
                        onChange={(e) => setEditDate(e.target.value)}
                        className="form-control form-control-sm"
                        style={{ fontSize: 12, padding: '2px 6px', width: 140, }}
                        autoFocus />

                    <div className="btn-group btn-group-sm" role="group">
                        <button type="button"
                            style={{ fontSize: 11, padding: '2px 6px', }}
                            className={`btn ${editMode === 'off' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => {
                                setEditMode('off');
                                setEditStartTime('');
                                setEditEndTime('');
                            }}>
                            Day off
                        </button>
                        <button type="button"
                            style={{ fontSize: 11, padding: '2px 6px', }}
                            className={`btn ${editMode === 'shift' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setEditMode('shift')}>
                            Shift
                        </button>
                    </div>

                    {editMode === 'shift' && (
                        <>
                            <input type="time"
                                value={editStartTime}
                                onChange={(e) => setEditStartTime(e.target.value)}
                                title="Start time"
                                className="form-control form-control-sm"
                                style={{ fontSize: 12, padding: '2px 6px', width: 110, }} />

                            <input type="time"
                                value={editEndTime}
                                onChange={(e) => setEditEndTime(e.target.value)}
                                title="End time"
                                className="form-control form-control-sm"
                                style={{ fontSize: 12, padding: '2px 6px', width: 110, }} />
                        </>
                    )}

                    <button type="button" className="btn btn-sm btn-success py-0 px-2"
                        style={{ fontSize: 12, }}
                        disabled={saving}
                        onClick={() => saveEdit(selectedEmployeeId)}>
                        Save
                    </button>

                    <button type="button"
                        className="btn btn-sm btn-outline-secondary py-0 px-2"
                        style={{ fontSize: 12, }}
                        disabled={saving}
                        onClick={cancelEdit}>
                        Cancel
                    </button>
                </span>
            );
        }

        const badgeBg = o.willAdd || o.willBecomeOff || o.pendingCustom ? '#fff3cd'
            : o.willRemove ? '#f8d7da'
                : isShift ? '#e6f4ea'
                    : '#e7f1fb';

        const badgeColor = o.willAdd || o.willBecomeOff || o.pendingCustom ? '#856404'
            : o.willRemove ? '#842029'
                : isShift ? '#1e7e34'
                    : '#0564bf';

        const timeLabel = showTime ? timeRangeLabel(o.start_time, o.end_time) : '';

        return (
            <span key={key}
                className="badge d-flex align-items-center gap-2"
                style={{
                    backgroundColor: badgeBg, color: badgeColor, fontWeight: 'normal',
                    fontSize: 13, padding: '6px 10px',
                    textDecoration: o.willRemove ? 'line-through' : 'none',
                    border: o.pending ? '1px dashed currentColor' : 'none',
                }}>
                {o.date}

                {timeLabel && (
                    <span style={{ fontSize: 11, opacity: 0.85 }}>
                        {timeLabel}
                    </span>
                )}

                {o.willAdd && (
                    <span style={{ fontSize: 10 }}>(new)</span>
                )}

                {o.willRemove && (
                    <span style={{ fontSize: 10 }}>(removing)</span>
                )}

                {o.willBecomeOff && (
                    <span style={{ fontSize: 10 }}>(will become day off)</span>
                )}

                {o.pendingCustom && !o.willBecomeOff && (
                    <span style={{ fontSize: 10 }}>(custom, unsaved)</span>
                )}

                {!o.pending && (
                    <button type="button"
                        onClick={() => startEdit(o)}
                        title="Update"
                        className="btn btn-sm p-0 border-0 bg-transparent d-flex align-items-center"
                        style={{ color: badgeColor, lineHeight: 1, }}
                        disabled={saving}>
                        <i className="fas fa-pen" style={{ fontSize: 11, }}></i>
                    </button>
                )}

                {/* Undo an unsaved custom shift time */}
                {o.pendingCustom && !o.willBecomeOff && (
                    <button type="button"
                        onClick={() => removePendingCustom(o.date)}
                        title="Undo custom time"
                        className="btn btn-sm p-0 border-0 bg-transparent d-flex align-items-center"
                        style={{ color: badgeColor, lineHeight: 1, }}
                        disabled={saving}>
                        <i className="fas fa-undo" style={{ fontSize: 11, }}></i>
                    </button>
                )}

                {/* Trash/undo only for day offs. Shift days are changed by clicking the calendar or the pen. */}
                {!isShift && (
                    <button type="button"
                        onClick={() => toggleIso(o.date)}
                        title={o.willRemove ? 'Undo removal' : 'Remove'}
                        className="btn btn-sm p-0 border-0 bg-transparent d-flex align-items-center"
                        style={{ color: o.willRemove ? '#0564bf' : '#dc3545', lineHeight: 1, }}
                        disabled={saving}>
                        <i className={o.willRemove ? 'fas fa-undo' : 'fas fa-trash'}
                            style={{ fontSize: 11, }}></i>
                    </button>
                )}
            </span>
        );
    };

    if (loading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: 700, }}
            >
                <Quantum size="45" speed="1.75" color="blue" />
            </div>
        );
    }

    return (
        <div className="d-flex flex-column min-vh-100 pb-2 px-3">
            <div className="flex-grow-1">
                <div className="container-fluid pb-2 px-2 px-md-4"
                    style={{ paddingTop: 20, }} >
                    <div className="row mb-4 shadow-sm border-bottom sticky-top py-3 px-md-3 px-2 rounded"
                        style={{ zIndex: 1050, background: 'linear-gradient(135deg, #0564bf, #89CFF0)', }} >
                        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h3 className="text-light mb-1"> Employee Day Offs </h3>
                                <p className="text-light mb-0">
                                    Click dates to select or unselect day offs, then press Save to apply.
                                </p>
                            </div>

                            {saving && (
                                <span className="text-light fw-semibold">
                                    <i className="fas fa-circle-notch fa-spin me-2"></i>
                                    Saving...
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12 col-lg-4 mb-4">
                            <div className="border rounded p-3 bg-white shadow-sm d-flex flex-column">
                                <h4 className="text-center mb-3"
                                    style={{ color: '#0564bf', }} >
                                    Employees
                                </h4>

                                <input type="text" placeholder="Search by name..."
                                    value={employeeSearch}
                                    onChange={(e) => setEmployeeSearch(e.target.value)}
                                    className="form-control form-control-sm mb-3" />

                                <div
                                    className="flex-grow-1 overflow-auto"
                                    style={{ maxHeight: '100vh', }}
                                    onWheel={handleWheel}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}

                                >
                                    {filteredEmployees.map((emp) => {
                                        const pendingCount = (dayOffsMap[emp.id] || []).length;
                                        const isSelected = Number(emp.id) === Number(selectedEmployeeId);

                                        const hasUnsaved =
                                            (pendingToggles[emp.id] || new Set()).size > 0 ||
                                            Object.keys(pendingCustom[emp.id] || {}).length > 0;
                                        return (
                                            <div key={emp.id}
                                                className="mb-2 p-2 border rounded d-flex justify-content-between align-items-center"
                                                style={{
                                                    cursor: 'pointer', background: isSelected ? 'rgba(5,100,191,0.08)' : 'transparent',
                                                    borderColor: isSelected ? '#0564bf' : undefined,
                                                }}
                                                onClick={() => selectEmployee(emp.id)} >
                                                <div className="d-flex align-items-center">
                                                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                                        style={{ width: 36, height: 36, backgroundColor: '#0564bf', color: 'white', fontSize: 13, fontWeight: 'bold', }}>
                                                        {(emp.full_name || emp.name || '?')
                                                            .charAt(0).toUpperCase()}
                                                    </div>

                                                    <div>
                                                        <div className="fw-semibold"
                                                            style={{ color: '#0564bf', fontSize: 14, }}  >
                                                            {emp.full_name || emp.name}
                                                        </div>

                                                        <small className="text-muted">
                                                            {emp.role || emp.username}
                                                        </small>
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-1">
                                                    {hasUnsaved && (
                                                        <span title="Unsaved changes"
                                                            className="rounded-circle"
                                                            style={{ width: 8, height: 8, backgroundColor: '#ffc107', display: 'inline-block', }} />
                                                    )}

                                                    {pendingCount > 0 && (
                                                        <span className="badge rounded-pill"
                                                            style={{ backgroundColor: '#89CFF0', color: '#0564bf', }} >
                                                            {pendingCount}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }
                                    )}

                                    {filteredEmployees.length === 0 && (
                                        <div className="text-center py-4 text-muted">
                                            <i className="fas fa-users"
                                                style={{ fontSize: 40, }} ></i>

                                            <p className="mt-2"> No employees found </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-8 mb-4">
                            <div className="border rounded p-3 bg-white shadow-sm">
                                <h4 className="text-center mb-1"
                                    style={{ color: '#0564bf', }}
                                >
                                    {selectedEmployee ? selectedEmployee.full_name || selectedEmployee.name : 'Select an employee'}
                                </h4>

                                <p className="text-center text-muted mb-4"
                                    style={{ fontSize: 13, }}  >
                                    Click a date to select or unselect it, then press Save to apply your changes.
                                </p>

                                {selectedEmployee && (
                                    <>
                                        <div className="mb-4">
                                            <button type="button"
                                                className="btn btn-sm btn-link text-decoration-none p-0"
                                                style={{ color: '#0564bf', }}
                                                onClick={() => setShowRecurringPicker((v) => !v)}>

                                                <i className={`fas fa-${showRecurringPicker ? 'minus' : 'plus'}-circle me-1`}></i>

                                                {showRecurringPicker ? 'Hide recurring weekday picker' : 'Add a recurring weekday'}
                                            </button>

                                            {showRecurringPicker && (
                                                <div className="rounded p-3 mt-2"
                                                    style={{ backgroundColor: '#f4f9fe', border: '1px solid #d9ecfb', }}>
                                                    <div className="row g-2 align-items-end">
                                                        <div className="col-6 col-md-3">
                                                            <label className="form-label text-muted small mb-1"> Day </label>

                                                            <select className="form-control form-control-sm"
                                                                value={recurringWeekday}
                                                                onChange={(e) => setRecurringWeekday(Number(e.target.value))} >

                                                                {WEEKDAYS.map(
                                                                    (wd, idx) => (
                                                                        <option key={wd} value={idx} >
                                                                            {wd}
                                                                        </option>
                                                                    )
                                                                )}
                                                            </select>
                                                        </div>

                                                        <div className="col-6 col-md-3">
                                                            <label className="form-label text-muted small mb-1"> From</label>

                                                            <input type="date"
                                                                className="form-control form-control-sm"
                                                                value={recurringStart}
                                                                onChange={(e) => setRecurringStart(e.target.value)}
                                                            />
                                                        </div>

                                                        <div className="col-6 col-md-3">
                                                            <label className="form-label text-muted small mb-1">To</label>
                                                            <input type="date" className="form-control form-control-sm"
                                                                value={recurringEnd}
                                                                onChange={(e) => setRecurringEnd(e.target.value)} />
                                                        </div>

                                                        <div className="col-6 col-md-3">
                                                            <button type="button"
                                                                className="btn btn-sm btn-primary w-100"
                                                                onClick={applyRecurringWeekday}
                                                                disabled={saving}>
                                                                Apply
                                                            </button>
                                                        </div>

                                                       

                                                        {recurringMode === 'shift' && (
                                                            <>
                                                                <div className="col-6 col-md-3">
                                                                    <label className="form-label text-muted small mb-1"> Start time </label>
                                                                    <input type="time"
                                                                        className="form-control form-control-sm"
                                                                        value={recurringStartTime}
                                                                        onChange={(e) => setRecurringStartTime(e.target.value)} />
                                                                </div>

                                                                <div className="col-6 col-md-3">
                                                                    <label className="form-label text-muted small mb-1"> End time </label>
                                                                    <input type="time"
                                                                        className="form-control form-control-sm"
                                                                        value={recurringEndTime}
                                                                        onChange={(e) => setRecurringEndTime(e.target.value)} />
                                                                </div>
                                                            </>
                                                        )}

                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Default shift for every working day of the month */}
                                        <div className="rounded p-3 mb-3"
                                            style={{ backgroundColor: '#f8fafc', border: '1px solid #e9eef4', }}>
                                            <div className="fw-semibold mb-2" style={{ fontSize: 13, color: '#0564bf' }}>
                                                Default shift
                                                <span className="text-muted fw-normal ms-2" style={{ fontSize: 12 }}>
                                                    applies to every working day without a custom time
                                                </span>
                                            </div>

                                            <div className="row g-2 align-items-end">
                                                <div className="col-6 col-md-4">
                                                    <label className="form-label text-muted small mb-1"> Shift start time </label>
                                                    <input type="time"
                                                        className="form-control form-control-sm"
                                                        value={shiftStartTime}
                                                        onChange={(e) => setShiftStartTime(e.target.value)} />
                                                </div>

                                                <div className="col-6 col-md-4">
                                                    <label className="form-label text-muted small mb-1"> Shift end time </label>
                                                    <input type="time"
                                                        className="form-control form-control-sm"
                                                        value={shiftEndTime}
                                                        onChange={(e) => setShiftEndTime(e.target.value)} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Custom shift time for one or more specific days */}
                                        <div className="rounded p-3 mb-3"
                                            style={{ backgroundColor: '#f6fbf7', border: '1px solid #d6ecdc', }}>
                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                <div>
                                                    <div className="fw-semibold" style={{ fontSize: 13, color: '#1e7e34' }}>
                                                        Custom shift for specific days
                                                    </div>
                                                    <small className="text-muted">
                                                        {customMode
                                                            ? 'Click working days on the calendar, set the times, then press Apply.'
                                                            : 'Give one or more days different hours from the default shift.'}
                                                    </small>
                                                </div>

                                                <button type="button"
                                                    className={`btn btn-sm ${customMode ? 'btn-success' : 'btn-outline-success'}`}
                                                    onClick={() => {
                                                        setCustomMode((v) => !v);
                                                        setCustomSelection([]);
                                                    }}>
                                                    {customMode ? 'Done' : 'Customize days'}
                                                </button>
                                            </div>

                                            {customMode && (
                                                <div className="row g-2 align-items-end mt-1">
                                                    <div className="col-6 col-md-3">
                                                        <label className="form-label text-muted small mb-1"> Custom start </label>
                                                        <input type="time"
                                                            className="form-control form-control-sm"
                                                            value={customStartTime}
                                                            onChange={(e) => setCustomStartTime(e.target.value)} />
                                                    </div>

                                                    <div className="col-6 col-md-3">
                                                        <label className="form-label text-muted small mb-1"> Custom end </label>
                                                        <input type="time"
                                                            className="form-control form-control-sm"
                                                            value={customEndTime}
                                                            onChange={(e) => setCustomEndTime(e.target.value)} />
                                                    </div>

                                                    <div className="col-12 col-md-6 d-flex align-items-center gap-2 flex-wrap">
                                                        <button type="button"
                                                            className="btn btn-sm btn-success"
                                                            onClick={applyCustomShift}
                                                            disabled={saving || customSelection.length === 0}>
                                                            Apply to {customSelection.length} day{customSelection.length === 1 ? '' : 's'}
                                                        </button>

                                                        <button type="button"
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => setCustomSelection([])}
                                                            disabled={customSelection.length === 0}>
                                                            Clear selection
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <button type="button" className="btn btn-sm btn-outline-primary"
                                                onClick={() => changeMonth(-1)}
                                            >
                                                <img className='d-flex justify-content-between align-items-center '
                                                    style={{ height: '20px', width: '20px' }}
                                                    src="https://img.icons8.com/?size=100&id=11538&format=png&color=228BE6" alt="" />
                                            </button>

                                            <span className="fw-semibold" style={{ color: '#0564bf', }} >
                                                {MONTH_NAMES[viewMonth]}{' '}{viewYear}
                                            </span>

                                            <button type="button" className="btn btn-sm btn-outline-primary"
                                                onClick={() => changeMonth(1)}>
                                                <img className='d-flex justify-content-between align-items-center '
                                                    style={{ height: '20px', width: '20px' }}
                                                    src="https://img.icons8.com/?size=100&id=11537&format=png&color=228BE6" alt="" />
                                            </button>
                                        </div>

                                        <div className="row g-1 mb-1 text-center"
                                            style={{ fontSize: 12 }}>
                                            {WEEKDAYS.map((wd) => (
                                                <div key={wd}
                                                    className="col text-muted small fw-semibold" >
                                                    {wd}
                                                </div>
                                            )
                                            )}
                                        </div>

                                        <div className="row g-1"
                                            style={{
                                                maxHeight: 'calc(100vh - 400px)',
                                                overflow: 'hidden',
                                            }}>
                                            {monthGrid.map((date, idx) => {
                                                const iso = toISO(date);
                                                const inMonth = date.getMonth() === viewMonth;
                                                const off = isDateOff(date);
                                                const isToday = isSameDay(date, today);

                                                const isPending = currentPending.has(iso);
                                                const inCustomSelection = customSelection.includes(iso);
                                                const customMark = inMonth && !off ? customTimeDates.get(iso) : undefined;

                                                return (
                                                    <div key={idx}
                                                        className="col-12" style={{ flex: '0 0 14.28%', maxWidth: '14.28%', }}>
                                                        <button type="button"
                                                            disabled={saving}
                                                            onClick={() => handleDateClick(date)}
                                                            className="btn w-100 p-0 d-flex align-items-center justify-content-center position-relative"
                                                            style={{
                                                                // aspectRatio: '1 / 1',
                                                                height: 'clamp(38px, 6.5vh, 55px)',
                                                                borderRadius: 8,
                                                                border: inCustomSelection ? '2px solid #28a745'
                                                                    : isPending ? '2px dashed #ffc107'
                                                                        : isToday ? '2px solid #0564bf' : '1px solid #e2e2e2',
                                                                background: off ? 'linear-gradient(135deg, #0564bf, #89CFF0)'
                                                                    : inCustomSelection ? '#e6f4ea' : 'white',
                                                                color: off ? 'white' : inMonth ? '#333' : '#bbb',
                                                                fontWeight: off || inCustomSelection ? 'bold' : 'normal',
                                                                // fontSize: 14,
                                                                fontSize: 'clamp(12px, 1.2vw, 14px)',

                                                                opacity: isPending ? 0.85 : 1,
                                                            }} >
                                                            {date.getDate()}

                                                            {customMark && (
                                                                <span title={customMark === 'pending' ? 'Custom shift (unsaved)' : 'Custom shift'}
                                                                    className="rounded-circle position-absolute"
                                                                    style={{
                                                                        width: 6, height: 6, bottom: 4,
                                                                        backgroundColor: customMark === 'pending' ? '#ffc107' : '#28a745',
                                                                    }} />
                                                            )}
                                                        </button>
                                                    </div>
                                                );
                                            }
                                            )}
                                        </div>

                                        <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
                                            {hasUnsavedChanges && (
                                                <span className="text-warning fw-semibold me-auto"
                                                    style={{ fontSize: 13 }} >
                                                    <i className="fas fa-exclamation-circle me-1"></i>
                                                    Unsaved changes
                                                </span>
                                            )}

                                            <button type="button" className="btn btn-sm btn-outline-secondary"
                                                onClick={discardChanges}
                                                disabled={saving || !hasUnsavedChanges}>
                                                Discard
                                            </button>

                                            <button type="button" className="btn btn-sm btn-primary"
                                                onClick={saveChanges}
                                                disabled={saving}>
                                                {saving ? 'Saving...' : 'Save Month'}
                                            </button>
                                        </div>

                                        {/* Day offs */}
                                        <div className="mt-4">
                                            <h6 className="text-muted mb-2"
                                                style={{ fontSize: 13, }}>
                                                {effectiveActiveCount}{' '}
                                                day
                                                {effectiveActiveCount === 1 ? '' : 's'}{' '}
                                                off selected
                                            </h6>

                                            <div className="d-flex flex-wrap gap-2">
                                                {effectiveOffs.map((o) => renderRecordBadge(o, true))}

                                                {effectiveOffs.length === 0 && (
                                                    <span className="text-muted"
                                                        style={{ fontSize: 13, }}>
                                                        None yet
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Shift days, grouped by shift start/end time, shown below the day offs */}
                                        <div className="mt-4">
                                            <h6 className="text-muted mb-2"
                                                style={{ fontSize: 13, }}>
                                                {effectiveShifts.length}{' '}
                                                shift day{effectiveShifts.length === 1 ? '' : 's'}
                                                {shiftGroups.length > 1 && ` in ${shiftGroups.length} different shift times`}
                                            </h6>

                                            {shiftGroups.map((group) => (
                                                <div key={group.key}
                                                    className="rounded p-2 mb-2"
                                                    style={{ backgroundColor: '#f6fbf7', border: '1px solid #d6ecdc', }}>
                                                    <div className="fw-semibold mb-2"
                                                        style={{ fontSize: 13, color: '#1e7e34', }}>
                                                        <i className="far fa-clock me-1"></i>
                                                        {group.label}
                                                        <span className="text-muted fw-normal ms-2"
                                                            style={{ fontSize: 12 }}>
                                                            {group.items.length} day{group.items.length === 1 ? '' : 's'}
                                                        </span>
                                                    </div>

                                                    <div className="d-flex flex-wrap gap-2">
                                                        {group.items.map((o) => renderRecordBadge(o, false))}
                                                    </div>
                                                </div>
                                            ))}

                                            {shiftGroups.length === 0 && (
                                                <span className="text-muted"
                                                    style={{ fontSize: 13, }}>
                                                    None yet
                                                </span>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <DashboardFooter />
        </div>
    );
};

export default EmployeeDayOff;